import React, { useState } from 'react';
import { 
  Search, 
  Filter, 
  Download, 
  Phone, 
  Mail, 
  MapPin,
  Star,
  Clock,
  UserCheck,
  Plus,
  Eye,
  Edit,
  Trash2
} from 'lucide-react';
import { useDoctors } from '../../hooks/useApi';
import useDoctorsCrud from '../../hooks/useDoctorsCrud';
import Avatar from '../common/Avatar';
import DoctorDetailsModalSimple from './DoctorDetailsModalSimple';
import DoctorEditModalSimple from './DoctorEditModalSimple';
import DoctorDeleteModalSimple from './DoctorDeleteModalSimple';
import DoctorAddModalSimple from './DoctorAddModalSimple';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const DoctorsList = () => {
  const { data: doctors = [], loading, error, refetch } = useDoctors();
  const { addDoctor, updateDoctor, deleteDoctor } = useDoctorsCrud();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSpecialty, setFilterSpecialty] = useState('');
  
  // États des modales
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const specialties = [...new Set(doctors.map(doctor => doctor.specialty))];

  const filteredDoctors = doctors.filter(doctor => {
    const name = doctor.user?.name || '';
    const specialty = doctor.specialty || '';
    const hospital = doctor.hospital || '';
    
    const matchesSearch = name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         specialty.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         hospital.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = !filterSpecialty || specialty === filterSpecialty;
    return matchesSearch && matchesFilter;
  });

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Titre
    doc.setFontSize(20);
    doc.text('Répertoire des Médecins Spécialistes', 20, 20);
    
    // Tableau
    const tableData = filteredDoctors.map(doctor => [
      doctor.user?.name || 'N/A',
      doctor.specialty || 'N/A',
      doctor.hospital || 'N/A',
      doctor.zone || 'N/A',
      doctor.user?.phone || 'N/A',
      doctor.experience || 'N/A',
      (doctor.rating || '0') + '/5'
    ]);

    autoTable(doc, {
      head: [['Nom', 'Spécialité', 'Hôpital', 'Zone', 'Téléphone', 'Expérience', 'Note']],
      body: tableData,
      startY: 30,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [59, 130, 246] }
    });

    doc.save('repertoire-medecins.pdf');
  };

  const handleViewDetails = (doctor) => {
    setSelectedDoctor(doctor);
    setIsDetailsModalOpen(true);
  };

  const handleEditDoctor = (doctor) => {
    setSelectedDoctor(doctor);
    setIsEditModalOpen(true);
  };

  const handleDeleteDoctor = (doctor) => {
    setSelectedDoctor(doctor);
    setIsDeleteModalOpen(true);
  };

  const handleAddDoctor = () => {
    setIsAddModalOpen(true);
  };

  const handleSaveDoctor = async (doctorData) => {
    const result = await addDoctor(doctorData);
    if (result.success) {
      // Recharger la liste des médecins
      refetch();
    }
  };

  const handleUpdateDoctor = async (doctorId, doctorData) => {
    const result = await updateDoctor(doctorId, doctorData);
    if (result.success) {
      // Recharger la liste des médecins
      refetch();
    }
  };

  const handleConfirmDelete = async (doctorId) => {
    const result = await deleteDoctor(doctorId);
    if (result.success) {
      // Recharger la liste des médecins
      refetch();
    }
  };

  const closeModals = () => {
    setIsDetailsModalOpen(false);
    setIsEditModalOpen(false);
    setIsDeleteModalOpen(false);
    setIsAddModalOpen(false);
    setSelectedDoctor(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className=" px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Répertoire des Médecins</h1>
              <p className="text-gray-600 mt-2">Trouvez et contactez les spécialistes de la RDC</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleAddDoctor}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200"
              >
                <Plus className="h-4 w-4" />
                <span>Ajouter Médecin</span>
              </button>
              <button
                onClick={exportToPDF}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200"
              >
                <Download className="h-4 w-4" />
                <span>Exporter PDF</span>
              </button>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Chargement des médecins...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Erreur de chargement</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filter */}
        {!loading && !error && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Rechercher un médecin..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <select
                  value={filterSpecialty}
                  onChange={(e) => setFilterSpecialty(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Toutes les spécialités</option>
                  {specialties.map(specialty => (
                    <option key={specialty} value={specialty}>{specialty}</option>
                  ))}
                </select>
              </div>
              <div className="text-sm text-gray-500 flex items-center">
                {filteredDoctors.length} médecin(s) trouvé(s)
              </div>
            </div>
          </div>
        )}

        {/* Doctors Grid */}
        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDoctors.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <div className="text-gray-400 mb-4">
                  <UserCheck className="h-16 w-16 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun médecin trouvé</h3>
                <p className="text-gray-500">Aucun médecin ne correspond à vos critères de recherche.</p>
              </div>
            ) : (
              filteredDoctors.map((doctor, index) => (
            <div
              key={doctor.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200"
            >
              <div className="flex items-start space-x-3">
                <Avatar
                  src={doctor.user?.avatar}
                  alt={doctor.user?.name || 'Médecin'}
                  name={doctor.user?.name || 'Médecin'}
                  size="lg"
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">{doctor.user?.name || 'Médecin'}</h3>
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center space-x-1">
                        <Star className="h-4 w-4 text-yellow-400 fill-current" />
                        <span className="text-sm font-medium text-gray-900">{doctor.rating}</span>
                      </div>
                      {/* Boutons d'action */}
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleViewDetails(doctor)}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors duration-200"
                          title="Voir les détails"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditDoctor(doctor)}
                          className="p-1 text-gray-400 hover:text-green-600 transition-colors duration-200"
                          title="Modifier"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteDoctor(doctor)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors duration-200"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <p className="text-primary-600 font-medium">{doctor.specialty}</p>
                  <p className="text-sm text-gray-600 mt-1">{doctor.hospital}</p>
                  
                  <div className="flex items-center mt-2 text-sm text-gray-500">
                    <MapPin className="h-4 w-4 mr-1" />
                    <span>{doctor.zone}</span>
                  </div>
                  
                  <div className="flex items-center mt-1 text-sm text-gray-500">
                    <Clock className="h-4 w-4 mr-1" />
                    <span>{doctor.experience}</span>
                  </div>

                  <div className="flex items-center mt-2">
                    <div className={`w-2 h-2 rounded-full mr-2 ${doctor.isOnline ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                    <span className="text-sm text-gray-500">
                      {doctor.isOnline ? 'En ligne' : 'Hors ligne'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-4 space-y-2">
                <div className="flex items-center text-sm text-gray-600">
                  <Phone className="h-4 w-4 mr-2" />
                  <span>{doctor.user?.phone || 'N/A'}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Mail className="h-4 w-4 mr-2" />
                  <span>{doctor.user?.email || 'N/A'}</span>
                </div>
              </div>
            </div>
              ))
            )}
          </div>
        )}

        {/* Modales */}
        <DoctorDetailsModalSimple
          doctor={selectedDoctor}
          isOpen={isDetailsModalOpen}
          onClose={closeModals}
        />

        <DoctorEditModalSimple
          doctor={selectedDoctor}
          isOpen={isEditModalOpen}
          onClose={closeModals}
          onSave={handleUpdateDoctor}
        />

        <DoctorDeleteModalSimple
          doctor={selectedDoctor}
          isOpen={isDeleteModalOpen}
          onClose={closeModals}
          onConfirm={handleConfirmDelete}
        />

        <DoctorAddModalSimple
          isOpen={isAddModalOpen}
          onClose={closeModals}
          onSave={handleSaveDoctor}
        />
      </div>
    </div>
  );
};

export default DoctorsList;
